import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MoveHorizontal } from "lucide-react";
import gsap from "gsap";
import { cn } from "@/lib/utils";
import { useIsDesktop } from "@/hooks/use-is-desktop";
import { useLeftSidebar } from "@/components/ui/sidebar";
import { prefersReducedMotion } from "@/features/module-hub/hooks/use-module-hub";
import { DOCK_DEFAULT_WIDTH } from "../constants";
import { useChatDock } from "../hooks/use-chat-dock";
import { useDockResize } from "../hooks/use-dock-resize";
import { isDockShortcut } from "../utils/shortcut";
import { ChatDockPanel } from "./ChatDockPanel";

/** Mobil sheet'in CSS transform süresi; kapanışta unmount bunu bekler. */
const MOBILE_SHEET_MS = 300;

/**
 * Sağ chat dock'u (Cursor yerleşimi: sidebar | içerik | sohbet).
 *
 * md+  → içeriği iten kolon. Açılış: genişlik expo ile açılır, sol kenarda
 *        modül renginde bir çizgi süpürülür, başlık ve gövde sağdan kayarak
 *        sırayla belirir. Kapanış: içerik geri kayar, kolon toplanır.
 * < md → header altından kayan tam yükseklik sheet + underlay (CSS transition).
 *
 * Panel `rendered` true iken mount kalır: kapanışta içerik animasyon bitene
 * kadar yerinde durur, sonra kaldırılır. ⌘J / Ctrl+J aç-kapa yapar.
 */
export const ChatDock: React.FC = () => {
  const { t } = useTranslation();
  const { isAvailable, isOpen, close, toggle } = useChatDock();
  const isDesktop = useIsDesktop();
  const { open: isLeftSidebarOpen } = useLeftSidebar();

  const [rendered, setRendered] = useState(isOpen);
  const asideRef = useRef<HTMLElement>(null);
  const edgeRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<gsap.core.Timeline | null>(null);
  const { width: dockWidth, isDragging, handleProps } = useDockResize(
    asideRef,
    isOpen && rendered && isDesktop,
  );
  const dockWidthRef = useRef(dockWidth);
  dockWidthRef.current = dockWidth;

  // Açılış isteği geldi → paneli mount et (animasyon bir sonraki effect'te)
  useEffect(() => {
    if (isOpen) setRendered(true);
  }, [isOpen]);

  // ── Desktop: GSAP açılış / kapanış ──
  useLayoutEffect(() => {
    const aside = asideRef.current;
    const edge = edgeRef.current;
    if (!aside || !edge || !isDesktop) return;

    timelineRef.current?.kill();
    const items = aside.querySelectorAll<HTMLElement>("[data-dock-anim]");
    const reduced = prefersReducedMotion();

    if (isOpen) {
      if (!rendered) return; // panel henüz mount olmadı
      const targetWidth = dockWidthRef.current || DOCK_DEFAULT_WIDTH;
      if (reduced) {
        gsap.set(aside, { width: targetWidth });
        return;
      }
      const tl = gsap.timeline({
        onComplete: () => {
          gsap.set(items, { clearProps: "transform,opacity" });
          // Kısayolla ya da tutamaçla açan hemen yazabilsin
          aside
            .querySelector<HTMLTextAreaElement>("textarea")
            ?.focus({ preventScroll: true });
        },
      });
      tl.fromTo(
        aside,
        { width: 0 },
        { width: targetWidth, duration: 0.5, ease: "expo.out" },
        0,
      )
        .fromTo(
          edge,
          { scaleY: 0, opacity: 1 },
          { scaleY: 1, duration: 0.45, ease: "expo.out" },
          0,
        )
        .to(edge, { opacity: 0, duration: 0.45, ease: "power2.out" }, 0.3)
        .fromTo(
          items,
          { x: 28, opacity: 0 },
          {
            x: 0,
            opacity: 1,
            duration: 0.4,
            ease: "power3.out",
            stagger: 0.07,
          },
          0.12,
        );
      timelineRef.current = tl;
      return;
    }

    if (!rendered) return;
    if (reduced) {
      gsap.set(aside, { clearProps: "width" });
      setRendered(false);
      return;
    }
    const tl = gsap.timeline({
      onComplete: () => {
        gsap.set(aside, { clearProps: "width" });
        setRendered(false);
      },
    });
    tl.to(
      items,
      {
        x: 24,
        opacity: 0,
        duration: 0.18,
        ease: "power2.in",
        stagger: { each: 0.04, from: "end" },
      },
      0,
    ).to(aside, { width: 0, duration: 0.36, ease: "expo.inOut" }, 0.06);
    timelineRef.current = tl;
  }, [isOpen, rendered, isDesktop]);

  useEffect(
    () => () => {
      timelineRef.current?.kill();
    },
    [],
  );

  // ── Mobil: sheet CSS ile kayar; kapanışta içerik transition bitince kalkar ──
  useEffect(() => {
    if (isDesktop || isOpen || !rendered) return;
    const t = window.setTimeout(() => setRendered(false), MOBILE_SHEET_MS);
    return () => window.clearTimeout(t);
  }, [isDesktop, isOpen, rendered]);

  // Mobilde iki sheet aynı anda açık kalmasın: sol sidebar açılırsa dock çekilir
  // (dock z-index'i sidebar'ın üstünde; aksi hâlde sidebar erişilmez kalıyor)
  useEffect(() => {
    if (!isDesktop && isOpen && isLeftSidebarOpen) close();
  }, [isDesktop, isOpen, isLeftSidebarOpen, close]);

  // Klavye: Escape kapatır (mobil sheet), ⌘J / Ctrl+J aç-kapa
  useEffect(() => {
    if (!isAvailable) return;
    const onKey = (e: KeyboardEvent) => {
      if (isDockShortcut(e)) {
        e.preventDefault();
        toggle();
        return;
      }
      if (e.key === "Escape" && isOpen && !isDesktop) close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isAvailable, isOpen, isDesktop, close, toggle]);

  if (!isAvailable) return null;

  return (
    <>
      {/* ── Mobil underlay ── */}
      <div
        className={cn(
          "md:hidden fixed left-0 right-0 bottom-0 top-14 z-[940] transition-opacity duration-200",
          isOpen
            ? "opacity-100 pointer-events-auto bg-black/20 backdrop-blur-sm"
            : "opacity-0 pointer-events-none",
        )}
        onClick={close}
      />

      {/* ── Mobil sheet ── */}
      <div
        className={cn(
          "md:hidden fixed top-14 right-0 bottom-0 z-[960] w-[92vw] max-w-md",
          "border-l border-border-secondary dark:border-gray-700 shadow-2xl shadow-black/25 rounded-l-2xl overflow-hidden",
          "transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform",
          isOpen ? "translate-x-0" : "translate-x-full",
        )}
        aria-hidden={!isOpen}
      >
        {rendered && !isDesktop && <ChatDockPanel />}
      </div>

      {/* ── Desktop kolon ── */}
      <aside
        ref={asideRef}
        className={cn(
          "hidden md:block relative z-20 shrink-0 h-full w-0 overflow-visible",
          rendered && "border-l border-border-secondary/70 dark:border-gray-800",
          isDragging && "select-none",
        )}
        aria-hidden={!isOpen}
      >
        {/* Tut-sürükle: hap, içerik/dock ayırıcısının tam üzerinde */}
        {rendered && isOpen && (
          <button
            type="button"
            {...handleProps}
            aria-label={t("chatDock.resize")}
            title={t("chatDock.resize")}
            className={cn(
              "absolute inset-y-0 left-0 z-20 hidden w-9 -translate-x-1/2 cursor-col-resize touch-none items-center justify-center md:flex",
              "focus-visible:outline-none",
            )}
          >
            <span
              aria-hidden
              className={cn(
                "absolute inset-y-0 left-1/2 w-px -translate-x-1/2 rounded-full transition-[width,background-color] duration-150",
                isDragging ? "w-0.5 bg-module-accent" : "bg-transparent",
              )}
            />
            <span
              aria-hidden
              className={cn(
                "relative flex h-4 aspect-video items-center justify-center rounded-full p-0",
                "border border-module-accent/30 bg-bg-primary text-module-accent",
                "shadow-sm dark:bg-gray-900",
                "transition-[background-color,border-color,color] duration-150",
                isDragging &&
                  "border-module-accent bg-module-accent text-module-accent-foreground",
              )}
            >
              <MoveHorizontal className="size-3" />
            </span>
          </button>
        )}
        {/* Açılışta sol kenarda süpürülen modül rengi */}
        <div
          ref={edgeRef}
          aria-hidden
          className="pointer-events-none absolute inset-y-0 left-0 z-10 w-px origin-top bg-module-accent opacity-0 shadow-[0_0_12px_var(--module-accent)]"
        />
        {/* Animasyon sırasında paneli kes; tutamaç aside overflow-visible ile taşar */}
        <div className="h-full overflow-hidden">
          <div className="h-full" style={{ width: dockWidth }}>
            {rendered && isDesktop && <ChatDockPanel />}
          </div>
        </div>
      </aside>
    </>
  );
};
